import { useEffect } from 'react';

/**
 * Re-run `refetch` whenever the user returns to the tab or window (window focus, or
 * the document becoming visible again). This keeps a screen from showing a stale
 * status after the user navigated away and something changed on the backend — e.g. a
 * Daedalus session that merged while its poll was stopped at a "terminal" state.
 *
 * Pass a STABLE callback (wrap in useCallback) so the listeners aren't re-subscribed
 * every render. `enabled` gates it (e.g. only when a session is open).
 */
export function useRefetchOnFocus(refetch: () => void, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => refetch();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refetch();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [refetch, enabled]);
}
